import * as React from 'react'
import { Spin, Form, Input } from 'antd'
import { isEmpty, get, max, orderBy } from 'lodash'
import { useImmer } from 'use-immer'
import { showError } from 'helpers/errors'
import { createLabelFactory } from 'helpers/formViews'
import { stopEvent } from 'helpers/events'
import { formatUserTime } from 'helpers/dateTime'
import { Header, Title } from 'helpers/print'
import { t } from 'helpers/i18n'
import { getSectionProps, getWidths, getLabel } from 'helpers/formTemplates'
import { Section } from 'components/FormInstances/FormView/Styled'
import DatePicker from 'elements/DatePicker'
import Select, { Option, getOptionProps } from 'elements/Select'
import Field from 'components/FormInstances/FormView/Field'
import { Row, Col } from 'elements/Grid'

function Component(props) {
  const { tenant, settingsType, parentRecord: item } = props

  const [state, updateState] = useImmer({})

  React.useEffect(() => {
    async function fetchSettings() {
      try {
        const responses = await Promise.all([
          props.getSettings({ type: settingsType }),
          props
            .getDocumentContents({
              request: {
                domainObjectId: tenant.id,
                domainObjectType: 'Customer',
                documentType: 'PurchaseOrderLogo',
                documentName: tenant.subdomain,
              },
            })
            .catch(() => {}),
          props.getFormTemplate(item.templateId),
          props.getUsers(),
        ])

        updateState((draft) => {
          draft.fieldSettings = get(responses[0], 'value.data.fieldSettings', [])
          draft.logo = get(responses[1], 'value.data', {})
          draft.formTemplate = get(responses[2], 'value.data', {})
          draft.users = get(responses[3], 'value.data.items', [])
        })
      } catch (error) {
        showError({ error })
      }
    }

    fetchSettings()
  }, [])

  const title = item.name
  const createLabel = createLabelFactory(state.fieldSettings)

  return isEmpty(state.fieldSettings) ? (
    <Spin />
  ) : (
    <>
      <Title>
        <Col xs={16}>
          <h2>{title}</h2>
        </Col>
        <Col xs={8} className="text-right">
          {state.logo && state.logo.fileType && state.logo.contents && (
            <img src={`data:${state.logo.fileType};base64,${state.logo.contents}`} alt="" />
          )}
        </Col>
      </Title>
      <Header>
        <Form layout="vertical" colon={false} onSubmit={stopEvent}>
          <Row>
            <Col xs={8}>
              <Form.Item label={createLabel('formTemplateName')}>
                <Input value={item.formTemplateName} readOnly />
              </Form.Item>
            </Col>
            <Col xs={16}>
              <Form.Item label={createLabel('formTemplateDescription')}>
                <Input value={item.formTemplateDescription} readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <Form.Item label={createLabel('userName')}>
                <Select value={item.userName} placeholder={t('none')} readOnly>
                  <Option value="">{t('none')}</Option>
                  {(state?.users ?? []).map((each) => (
                    <Option key={each.userName} value={each.userName}>
                      <span {...getOptionProps(each)}>{each.displayName}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item label={createLabel('dueDate')}>
                <DatePicker value={item.dueDate} placeholder="" readOnly />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item label={createLabel('done')}>
                <Input
                  value={item.done ? formatUserTime(item.lastSubmitDate, item.lastUpdateBy) : null}
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Header>
      {(state?.formTemplate?.formPages ?? []).map((formPage) => (
        <div key={formPage.id}>
          <div className="font-size-16 my-24 text-black">{formPage.name}</div>
          {(formPage?.formPageSections ?? []).map((formPageSection) => {
            const numberOfLines = !isEmpty(formPageSection.formQuestions)
              ? max(formPageSection.formQuestions.map((each) => each.line)) + 2
              : 1

            return (
              <Section key={formPageSection.id} {...getSectionProps(state.formTemplate, formPageSection)}>
                {[...Array(numberOfLines).keys()].map((line) => {
                  const formQuestions = orderBy(formPageSection?.formQuestions ?? [], [
                    'line',
                    'position',
                  ]).filter((each) => each.line === line)

                  return (
                    <Row key={line}>
                      {formQuestions.map((formQuestion) => {
                        const formAnswer = (item?.formAnswers ?? []).find(
                          (one) => one.questionId === formQuestion.id
                        )
                        const { fieldWidth, labelWidth, inputWidth } = getWidths({
                          formTemplate: state.formTemplate,
                          formQuestion,
                        })
                        const color = formPageSection.overrideFormColor
                          ? formPageSection.contentForegroundColor
                          : state.formTemplate?.formForegroundColor
                        const label = getLabel({ formQuestion, labelWidth, color })
                        return (
                          <Col key={formQuestion.id} xs={fieldWidth}>
                            <Form.Item
                              label={label}
                              labelCol={{ span: labelWidth }}
                              wrapperCol={{ span: inputWidth }}
                              colon={false}
                            >
                              <Field
                                question={formQuestion}
                                answer={formAnswer}
                                layout={labelWidth === 24 ? 'vertical' : 'horizontal'}
                                color={color}
                                readOnly
                              />
                            </Form.Item>
                          </Col>
                        )
                      })}
                    </Row>
                  )
                })}
              </Section>
            )
          })}
        </div>
      ))}
      {state.formTemplate?.footer && <div className="my-12">{state.formTemplate.footer}</div>}
    </>
  )
}

export default Component
